// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { orderStatusCode } from './constants';

export type ProjectStatusKind = 'inProgress' | 'delivered' | 'canceled' | 'estimate' | 'notSubmitted';

export interface IProjectStatus {
  kind: ProjectStatusKind;
  // What the chip says on the map rail and the pin card.
  label: string;
  // Which step the grid card's four-step progress bar sits on. The chip collapses everything
  // between placement and delivery into one "In Progress"; the grid distinguishes the stages
  // within it, so both readings come from here rather than from two separate mappings.
  step: 0 | 1 | 2 | 3;
}

// How far the order itself has got. Independent of the estimate flag: a project can hold a
// placed order and a saved estimate at once, and the work the placed order has done is still
// real - the flag changes what the chip calls the project, not how far along it is.
const resolveStep = (orderStatus?: number | null): 0 | 1 | 2 | 3 => {
  if (orderStatus === orderStatusCode.processing || orderStatus === orderStatusCode.autonomousFailed) return 1;
  if (orderStatus === orderStatusCode.autonomousSuccessful) return 2;
  if (
    orderStatus === orderStatusCode.manualDrafting
    || orderStatus === orderStatusCode.usQc
    || orderStatus === orderStatusCode.qcComplete
    || orderStatus === orderStatusCode.done
  ) {
    return 3;
  }
  return 0;
};

// One reading of "what state is this project in", for every surface that shows one.
//
// Takes the fields rather than a project, because the two callers hold different shapes: the
// grid derives orderStatus from a project's orders array, while the map and rail receive it
// already flattened onto the coordinate by the server.
export const resolveProjectStatus = (
  { orderStatus, isEstimate }: { orderStatus?: number | null; isEstimate?: boolean },
): IProjectStatus => {
  const step = resolveStep(orderStatus);

  if (isEstimate) return { kind: 'estimate', label: 'Estimate', step };

  // A created order is a cart that was never placed - the same story as no order at all.
  if (orderStatus === undefined || orderStatus === null || orderStatus === orderStatusCode.created) {
    return { kind: 'notSubmitted', label: 'Not submitted', step };
  }

  if (orderStatus === orderStatusCode.done) return { kind: 'delivered', label: 'Completed', step };
  if (orderStatus === orderStatusCode.canceled) return { kind: 'canceled', label: 'Canceled', step };

  // Everything between placement and delivery reads as one state to a customer; the internal
  // drafting and QC codes are ops detail. The step still tracks them, because the grid card
  // shows how far along the work is.
  return { kind: 'inProgress', label: 'In Progress', step };
};
